import "@/App.css";
import { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import useScrollReveal from "@/hooks/useScrollReveal";
import {
  getEnabledLocales,
  getLocale,
  parsePath,
  localizePath,
  isPrefix,
  isEnabled,
  browserMatch,
  ogLocale,
  DEFAULT_LOCALE,
  STORAGE_KEY,
} from "@/i18n/helpers";
import { setMeta, ensureStylesheet } from "@/i18n/dom";

// Syncs i18n language + <html lang/dir> + og:locale with the URL, and performs
// silent first-visit language detection on unprefixed URLs.
function LanguageController() {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useEffect(() => {
    const segs = location.pathname.split("/").filter(Boolean);
    const hasPrefix = segs[0] && isPrefix(segs[0]);
    const { locale, page } = parsePath(location.pathname);

    if (!hasPrefix) {
      let pref;
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        pref = stored && isEnabled(stored) ? stored : browserMatch();
      } catch (e) {
        pref = browserMatch();
      }
      if (pref && pref !== DEFAULT_LOCALE) {
        navigate(localizePath(page, pref), { replace: true });
        return;
      }
    }

    if (i18n.language !== locale) i18n.changeLanguage(locale);
    const cfg = getLocale(locale) || getLocale(DEFAULT_LOCALE);
    document.documentElement.lang = locale;
    document.documentElement.dir = cfg.dir;
    // Load locale-specific webfonts only when that locale is active.
    if (locale === "zh") {
      ensureStylesheet(
        "font-noto-sans-sc",
        "https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@300;400;500;700&display=swap"
      );
    }
    if (locale === "ja") {
      ensureStylesheet(
        "font-ibm-plex-sans-jp",
        "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+JP:wght@300;400;500;600&display=swap"
      );
    }
    setMeta({ property: "og:locale" }, ogLocale(locale));
    setMeta({ property: "og:site_name" }, "IBM AI Action Plan");
  }, [location.pathname, navigate, i18n]);

  return null;
}

function Layout({ children }) {
  const location = useLocation();
  const { t } = useTranslation();
  useScrollReveal(location.pathname);

  return (
    <div className="app-shell">
      <a
        href="#main-content"
        className="skip-link"
        data-testid="skip-to-content"
      >
        {t("common.skipLink")}
      </a>
      <Header />
      <main
        id="main-content"
        className="app-main"
        tabIndex={-1}
        key={location.pathname}
      >
        {children}
      </main>
      <Footer />
    </div>
  );
}

function App() {
  const nonDefault = getEnabledLocales().filter(
    (l) => l.code !== DEFAULT_LOCALE
  );

  return (
    <div className="App">
      <BrowserRouter>
        <LanguageController />
        <Routes>
          <Route path="/" element={<Layout><Home /></Layout>} />
          {/* Legacy Share route -> redirect to the default-locale home. */}
          <Route
            path="/share-your-challenge"
            element={<Navigate to="/" replace />}
          />
          {nonDefault.flatMap((l) => [
            <Route
              key={l.code}
              path={`/${l.code}`}
              element={<Layout><Home /></Layout>}
            />,
            // Legacy localized Share route -> redirect to the SAME locale home.
            <Route
              key={`${l.code}-share`}
              path={`/${l.code}/share-your-challenge`}
              element={<Navigate to={`/${l.code}`} replace />}
            />,
          ])}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
